import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiGet, apiPost } from "../lib/api";
import { signOut } from "../lib/auth";
import { ErrorState } from "./Loading";

/** Typed exactly, not just "delete" -- see the comment on the confirmation input below. */
const DELETE_CONFIRMATION = "DELETE MY ACCOUNT";

/**
 * Triggers a browser download of the export without ever putting it on a server.
 *
 * A Blob URL is created from the JSON already in memory and revoked immediately after -- nothing is
 * uploaded, no link is emailed, and there is no staging copy of a family's data sitting anywhere waiting
 * to expire.
 */
function downloadJson(data: unknown, filename: string) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Export my data, and delete my account (PRD §12).
 *
 * `canDelete` is false for a coach. A coach account owns every client on the roster, so deleting it
 * would take other people's data with it -- the server refuses outright, and the button is simply not
 * offered rather than rendered and then rejected.
 */
export function DataRights({ canDelete }: { canDelete: boolean }) {
  const [confirmation, setConfirmation] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  const exportData = useMutation({
    mutationFn: () => apiGet<unknown>("/account/export"),
    onSuccess: (data) => downloadJson(data, "athletes-advantage-export.json"),
  });

  const deleteAccount = useMutation({
    mutationFn: () => apiPost("/account/delete", { confirm: true }),
    /**
     * Signing out is not decoration here. The account and its Cognito user are gone by this point, so
     * every subsequent request would fail on a token that no longer refers to anything -- the app would
     * appear broken rather than deleted. Sending them to the signed-out home page is the honest end state.
     */
    onSuccess: () => {
      signOut();
      window.location.href = "/";
    },
  });

  return (
    <>
      <h2>Your data</h2>
      <p className="form-hint">
        {/* Deliberately role-neutral. This component is shared, and the copy used to end "your
            conversations with your coach" -- which reads as nonsense on the coach's own settings page. */}
        Download everything we hold about you as a JSON file: your account, your athletes, their plans,
        their logged sessions, and your messages.
      </p>
      {exportData.isError && <ErrorState error={exportData.error} />}
      <button type="button" onClick={() => exportData.mutate()} disabled={exportData.isPending}>
        {exportData.isPending ? "Preparing…" : "Export my data"}
      </button>

      {canDelete && (
        <>
          <h2>Delete my account</h2>
          <p className="form-hint">
            This deletes your account, every athlete on it, their plans, their training history and your
            messages. It also cancels your subscription. It cannot be undone, and we can&rsquo;t recover
            any of it afterwards &mdash; export your data first if you want to keep a copy.
          </p>

          {deleteAccount.isError && <ErrorState error={deleteAccount.error} />}

          {!showDelete ? (
            <button type="button" className="chip danger" onClick={() => setShowDelete(true)}>
              Delete my account
            </button>
          ) : (
            <div className="confirm-box" role="group" aria-label="Confirm account deletion">
              {/*
                A typed phrase rather than a second button. Every other confirmation in this app guards
                something recoverable -- a discarded draft can be rebuilt, a cancelled subscription runs
                to the end of its period. This one destroys a child's training history permanently, and
                two clicks in the same place is not a meaningful pause before that.
              */}
              <label htmlFor="delete-confirmation">
                Type <strong>{DELETE_CONFIRMATION}</strong> to confirm
              </label>
              <input
                id="delete-confirmation"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                autoComplete="off"
              />
              <button
                type="button"
                className="chip danger"
                disabled={confirmation !== DELETE_CONFIRMATION || deleteAccount.isPending}
                onClick={() => deleteAccount.mutate()}
              >
                {deleteAccount.isPending ? "Deleting…" : "Permanently delete my account"}
              </button>
              <button
                type="button"
                className="chip"
                onClick={() => {
                  setShowDelete(false);
                  setConfirmation("");
                }}
              >
                Never mind
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
